import {
  alpha,
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { getStacksViewSx } from '@src/components/_shared/sxStyles/Config/stacks';
import { useConfigContext } from '@src/pages/Home/pages/Config/ConfigWrapperContext';
import storeConnector from '@src/store/storeConnector';
import { Actions } from '@src/types/Actions';
import { ControlAuthorities } from '@src/types/ControlModes';
import { SiteMeta } from '@src/types/SiteMeta';
import { useEffect, useState } from 'react';

interface ControlModesAuthorityProps {
  actions: Actions;
  siteMeta: SiteMeta;
}

function ControlModesAuthority({
  siteMeta,
  actions,
}: ControlModesAuthorityProps) {
  const theme = useTheme();
  const stacksViewSx = getStacksViewSx(theme);
  const { controlAuthority } = useConfigContext();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedAuthority, setSelectedAuthority] = useState<
    ControlAuthorities | undefined
  >(undefined);

  const handleAuthorityOnClick = (authority: string): void => {
    const chosenAuthority =
      ControlAuthorities[authority as keyof typeof ControlAuthorities];

    if (chosenAuthority === selectedAuthority) {
      return;
    }
    setSelectedAuthority(chosenAuthority);
    setConfirmOpen(true);
  };

  const handleCancelOnClick = (): void => {
    setConfirmOpen(false);
    setSelectedAuthority(controlAuthority);
  };

  const handleConfirmOnClick = async (): Promise<void> => {
    const commands: string[] = [];

    commands.push(
      `WriteToVariant::ControlMode_Authority::${selectedAuthority}`,
    );

    await actions.sendCommand(commands, siteMeta.siteId);

    await setConfirmOpen(false);
  };

  useEffect(() => {
    setSelectedAuthority(controlAuthority);
  }, [controlAuthority]);

  return (
    <section>
      <Stack
        direction='row'
        sx={stacksViewSx.view.controlModesAuthority.wrapper}
      >
        <Box
          sx={stacksViewSx.view.controlModesAuthority.wrapper.authorityWrapper}
        >
          <Typography
            sx={stacksViewSx.view.controlModesAuthority.wrapper.authorityTitle}
          >
            AUTHORITY
          </Typography>
        </Box>
        <Box sx={stacksViewSx.view.controlModesAuthority.wrapper.buttonGroup}>
          <ButtonGroup disabled={siteMeta.ui.features?.SCADA_Controls_Disabled}>
            {Object.keys(ControlAuthorities).map((key) => {
              if (!isNaN(Number(key))) {
                return null;
              }

              if (
                !siteMeta.ui.features?.ScadaConfig_Enabled &&
                key === 'scada'
              ) {
                return null;
              }

              if (!siteMeta.ui.Home_Calendar_Enable && key === 'calendar') {
                return null;
              }

              const isSelected =
                selectedAuthority ===
                ControlAuthorities[key as keyof typeof ControlAuthorities];

              return (
                <Button
                  key={key}
                  size='medium'
                  variant={isSelected ? 'contained' : 'outlined'}
                  color={isSelected ? 'secondary' : 'inherit'}
                  onClick={() => {
                    handleAuthorityOnClick(key);
                  }}
                  sx={{
                    ...stacksViewSx.view.controlModesAuthority.wrapper
                      .buttonGroup.button,
                    ...(isSelected &&
                      siteMeta.ui.features?.SCADA_Controls_Disabled && {
                        backgroundColor: `${alpha(theme.palette.secondary.main, 0.63)}!important`,
                      }),
                  }}
                >
                  {key}
                </Button>
              );
            })}
          </ButtonGroup>
        </Box>
      </Stack>
      {confirmOpen && selectedAuthority !== undefined && (
        <Dialog open>
          <DialogTitle>
            Enable {ControlAuthorities[selectedAuthority].toUpperCase()}?
          </DialogTitle>
          <DialogActions>
            <Button onClick={handleCancelOnClick} color='inherit'>
              Cancel
            </Button>
            <Button onClick={handleConfirmOnClick} color='primary'>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </section>
  );
}

ControlModesAuthority.displayName = 'ControlModesAuthority';

export default storeConnector(ControlModesAuthority, {
  config: ['siteMeta'],
});
